import React from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { Input } from "../../atoms/Input/index.js";
import { Kbd } from "../../atoms/Kbd/index.js";
import { cn } from "../../lib/utils.js";
import type { OverlayPresence } from "../../primitives/OverlaySurface.js";
import { getLayerRoot, type PositionResult } from "../../primitives/index.js";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import type { PeopleFieldModel } from "./people-field-model.js";
import { PeopleFieldRow } from "./PeopleFieldRow.js";
import { PeopleFieldSection } from "./PeopleFieldSection.js";
import { PeopleFieldStatus } from "./PeopleFieldStatus.js";

/* The open list: a portalled, non-modal dialog under the field holding the
   search input (the combobox), the listbox, the workspace search's states,
   the letters hint and the keyboard legend. Positioning, presence and every
   decision stay with PeopleField; this file only draws.
   states-check-ignore: the one focusable control here is the design-system
   Input, whose focus treatment lives in lib/field-frame.ts; the options are
   never focused (aria-activedescendant, PeopleFieldRow). */

export interface PeopleFieldPanelIds {
  readonly dialog: string;
  readonly input: string;
  readonly list: string;
  readonly option: (index: number) => string;
}

export interface PeopleFieldPanelProps {
  readonly present: boolean;
  readonly motionProps: OverlayPresence["motionProps"];
  readonly position: PositionResult | null;
  readonly surfaceRef: React.RefObject<HTMLDivElement>;
  readonly inputRef: React.RefObject<HTMLInputElement>;
  readonly listRef: React.RefObject<HTMLDivElement>;
  readonly ids: PeopleFieldPanelIds;
  readonly dialogLabel: string;
  readonly labels: PeopleFieldResolvedLabels;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly onInputKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  /** Keys on Retry, the list's last control: Tab past it closes the list. */
  readonly onRetryKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  readonly model: PeopleFieldModel;
  readonly activeIndex: number;
  readonly onActiveIndexChange: (index: number) => void;
  readonly onPick: (index: number) => void;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreFailed: boolean;
  readonly hasRoster: boolean;
  readonly onRetry: () => void;
}

export function PeopleFieldPanel({
  present,
  motionProps,
  position,
  surfaceRef,
  inputRef,
  listRef,
  ids,
  dialogLabel,
  labels,
  query,
  onQueryChange,
  onInputKeyDown,
  onRetryKeyDown,
  model,
  activeIndex,
  onActiveIndexChange,
  onPick,
  loading,
  loadingMore,
  loadMoreFailed,
  hasRoster,
  onRetry,
}: PeopleFieldPanelProps): React.ReactElement | null {
  if (!present || typeof document === "undefined") return null;
  const trimmed = query.trim();
  let index = 0;
  return createPortal(
    <div
      ref={surfaceRef}
      id={ids.dialog}
      role="dialog"
      aria-label={dialogLabel}
      {...motionProps}
      style={{
        position: "fixed",
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? "visible" : "hidden",
      }}
      className={cn(
        motionProps.className,
        "z-popover flex w-96 max-w-[calc(100vw-1rem)] flex-col overflow-hidden",
        "rounded-md border border-pb-border bg-pb-card-elevated text-pb-text shadow-lg pb-hairline",
      )}
    >
      <div className="border-b border-pb-border p-1">
        <Input
          ref={inputRef}
          id={ids.input}
          variant="ghost"
          type="search"
          role="combobox"
          autoComplete="off"
          aria-label={labels.searchPlaceholder}
          aria-autocomplete="list"
          aria-expanded={true}
          aria-controls={ids.list}
          aria-activedescendant={
            model.items.length > 0 ? ids.option(activeIndex) : undefined
          }
          placeholder={labels.searchPlaceholder}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onInputKeyDown}
          prefix={<Search className="h-4 w-4" />}
        />
      </div>
      <div className="max-h-95 overflow-y-auto p-1.5">
        <div
          ref={listRef}
          id={ids.list}
          role="listbox"
          aria-label={dialogLabel}
          aria-busy={loading || loadingMore || undefined}
        >
          {model.sections.map((section) => (
            <PeopleFieldSection
              key={section.key}
              section={section}
              headerId={`${ids.list}-${section.key}`}
            >
              {section.items.map((item) => {
                const at = index++;
                return (
                  <PeopleFieldRow
                    key={item.key}
                    item={item}
                    id={ids.option(at)}
                    active={at === activeIndex}
                    query={trimmed}
                    labels={labels}
                    loadingMore={loadingMore}
                    loadMoreFailed={loadMoreFailed}
                    onHover={() => onActiveIndexChange(at)}
                    onPick={() => onPick(at)}
                  />
                );
              })}
            </PeopleFieldSection>
          ))}
        </div>
        <PeopleFieldStatus
          status={model.status}
          title={model.workspaceTitle}
          query={trimmed}
          hasRoster={hasRoster}
          labels={labels}
          onRetry={onRetry}
          onRetryKeyDown={onRetryKeyDown}
        />
        {model.hint ? (
          <p
            role="status"
            className="px-2 py-1.5 text-caption text-pb-text-muted"
          >
            {model.hint}
          </p>
        ) : null}
      </div>
      <div
        aria-hidden="true"
        className="flex gap-3.5 border-t border-pb-border px-3 py-2 text-dense-1 text-pb-text-muted"
      >
        <span className="inline-flex items-center gap-1">
          <Kbd size="sm">↑</Kbd>
          <Kbd size="sm">↓</Kbd>
          {labels.keyMove}
        </span>
        <span className="inline-flex items-center gap-1">
          <Kbd size="sm">Enter</Kbd>
          {labels.keyPick}
        </span>
        <span className="inline-flex items-center gap-1">
          <Kbd size="sm">Esc</Kbd>
          {labels.keyClose}
        </span>
      </div>
    </div>,
    getLayerRoot(),
  );
}
